import React from "react";
import { statusClasses } from "./PlannerFields";
import { createPageUrl } from "@/utils";

export default function ActionsNotesTile({
  guidance,
  notes,
  setNotes,
  selectedAdapter,
  matterId,
}) {
  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden border-t-4 border-t-purple-600 flex flex-col h-full">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-800">Actions & Notes</h2>
        <div className="text-slate-300">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
          </svg>
        </div>
      </div>

      <div className="p-5 flex-1 grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Column: VLA Guidance Panel */}
        <div className="space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">VLA Guidance Checklist</h3>
          
          <div className="space-y-3">
            <div>
              <span className="block text-xs font-medium text-slate-500 mb-1">Status Recommendation</span>
              <div className={`inline-flex px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide border ${statusClasses(guidance.status)}`}>
                {guidance.status.replaceAll("_", " ")}
              </div>
            </div>

            <div>
              <span className="block text-xs font-medium text-slate-500 mb-1">Next Action Required</span>
              <div className="text-slate-800 font-bold text-sm bg-slate-50 border border-slate-200/80 rounded-xl p-3 space-y-2">
                <div>{guidance.nextAction}</div>
                {guidance.missingWorksheet && (
                  <div className="text-xs text-purple-700 font-medium border-t border-slate-200/60 pt-2 mt-2 leading-relaxed">
                    Reminder: A psychological assessment has been approved. Please ensure you have a completed{" "}
                    <a
                      href={`${createPageUrl("VLAReportWorksheet")}?matterId=${matterId || ""}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline text-purple-900 font-bold hover:text-purple-800 transition-colors font-sans"
                    >
                      VLA Report Worksheet
                    </a>{" "}
                    on file.
                  </div>
                )}
              </div>
            </div>

            {guidance.reasons.length > 0 && (
              <div>
                <span className="block text-xs font-bold uppercase tracking-wide text-slate-400 mb-1.5">Guidelines & Assessment Reasons</span>
                <ul className="space-y-1.5 text-xs font-medium text-slate-600 list-disc pl-5">
                  {guidance.reasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              </div>
            )}

            {guidance.warnings.length > 0 && (
              <div>
                <span className="block text-xs font-bold uppercase tracking-wide text-rose-500 mb-1.5">Warnings & Risks</span>
                <ul className="space-y-1.5 text-xs font-bold text-rose-600 list-disc pl-5">
                  {guidance.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Matter Notes & Integrations */}
        <div className="space-y-4 flex flex-col h-full border-t md:border-t-0 md:border-l border-slate-100 pt-4 md:pt-0 md:pl-6">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Matter Notes</h3>
          
          <textarea
            value={notes || ""}
            onChange={(e) => setNotes(e.target.value)}
            rows={5}
            className="w-full flex-1 border border-slate-200 rounded-xl px-3 py-2 text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none transition-all"
            placeholder="Freeform notes for the downloaded planner file..."
          />

          {selectedAdapter && (
            <div className="rounded-xl border border-purple-100 bg-purple-50/50 p-3.5 space-y-2 mt-auto">
              <h4 className="text-xs font-bold text-purple-950 uppercase tracking-wide">Practice Manager Connection</h4>
              <p className="text-xs text-purple-800 leading-relaxed font-medium">
                Connected to <span className="font-bold">{selectedAdapter.name}</span>. Matter details are automatically populated upon Practice Manager sync.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
